import { CreatePlacementsPrompt } from 'modules/placements/components/CreatePlacementsPrompt';
import { TEMPLATE_MENU_OPTIONS } from 'modules/templateIndex/constants';
import { createTemplate } from 'modules/templateIndex/ducks/actions';
import { selectIsTemplateAsyncProcessing } from 'modules/templateIndex/ducks/selectors';
import { getLocaleLabel } from 'modules/templateIndex/utils';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ModalFooter, ModalHeader } from 'ui/modal';
import { PrimaryButton, SecondaryButton } from 'ui/button';
import { BaseOption } from 'ui/selects';
import { useLocaleSettingsContext } from 'modules/campaigns/lib/contexts/LocaleSettingsContext';
import { selectIsEmbeddedMessagingCoreFeatureFlagOn } from 'core/userContext';
import { CreateTemplateForm } from './CreateTemplateForm';
import { StyledModal, StyledModalBody } from './NewEmbeddedMessageTemplateModal.styled';
import { EMBEDDED_MODAL_CONTENT } from './constants';
import { Spinner } from 'ui/spinner';
import { useQueryPlacements } from 'modules/placements/hooks/useQueryPlacements';

const { createPlacementModalHeader, modalHeaderLabel, okayLabel } = EMBEDDED_MODAL_CONTENT;

type NewEmbeddedMessageTemplateModalProps = {
  closeModal: () => void;
  content: Record<string, any>;
  currentFolderId: number | null;
  id: string;
};

export const NewEmbeddedMessageTemplateModal = ({
  closeModal,
  content,
  currentFolderId,
  ...props
}: NewEmbeddedMessageTemplateModalProps) => {
  const dispatch = useDispatch();
  const {
    createTemplateButtonText,
    modalHeader: { prefix, suffix },
    templateNameInput,
  } = content;

  const isEmbeddedMessagingEnabled = useSelector(selectIsEmbeddedMessagingCoreFeatureFlagOn);
  const isTemplateAsyncProcessing = useSelector(selectIsTemplateAsyncProcessing);

  const { placements: placementsList, loading: placementsLoading } = useQueryPlacements();

  const isCreateTemplateFormEnabled = !isEmbeddedMessagingEnabled || !!placementsList?.length;

  const { locales: projectLocales } = useLocaleSettingsContext();
  const defaultLocaleTag = projectLocales?.[0]?.tag || '';
  const localeOptions: BaseOption[] = projectLocales?.map((locale) => ({
    id: locale.tag,
    label: getLocaleLabel(locale),
    value: locale.tag,
  }));

  const [templateName, setTemplateName] = useState('');
  const [selectedLocaleTag, setSelectedLocaleTag] = useState<string>(defaultLocaleTag);
  const [selectedPlacementId, setSelectedPlacementId] = useState<number | undefined>(undefined);
  const [hasUnselectedPlacementError, setHasUnselectedPlacementError] = useState(false);

  // TODO: Once locales are re-introduced, check that selectedLocaleTag is set before enabling create button
  const isCreateButtonEnabled = templateName.length > 0 && templateName.length <= 255;

  const handleClick = () => {
    if (!selectedPlacementId) {
      setHasUnselectedPlacementError(true);
    } else {
      dispatch(
        createTemplate({
          currentFolderId,
          name: templateName,
          messageMedium: TEMPLATE_MENU_OPTIONS.embedded.slug,
          placementId: selectedPlacementId,
        }),
      );
    }
  };

  useEffect(() => {
    document.getElementById('embedded-message-template-name-input')?.focus();
  }, []);

  const renderBody = () => {
    if (placementsLoading) return <Spinner />;
    else if (isCreateTemplateFormEnabled)
      return (
        <CreateTemplateForm
          nameInputContent={templateNameInput}
          templateName={templateName}
          setTemplateName={setTemplateName}
          selectPlacement={handlePlacementSelect}
          localeOptions={localeOptions}
          selectedLocaleTag={selectedLocaleTag}
          setSelectedLocaleTag={setSelectedLocaleTag}
          hasUnselectedPlacementError={hasUnselectedPlacementError}
          placements={placementsList}
        />
      );
    else return <CreatePlacementsPrompt />;
  };

  const handlePlacementSelect = (placementId: number) => {
    setHasUnselectedPlacementError(false);
    setSelectedPlacementId(placementId);
  };

  return (
    <StyledModal
      data-test="embedded-message-template-modal"
      isModalOpen
      onClose={closeModal}
      {...props}
    >
      <ModalHeader data-test="embedded-message-template-modal-header">
        {isCreateTemplateFormEnabled
          ? `${prefix} ${modalHeaderLabel} ${suffix}`
          : createPlacementModalHeader}
      </ModalHeader>
      <StyledModalBody data-test="embedded-message-template-modal-body">
        {renderBody()}
      </StyledModalBody>
      <ModalFooter
        data-test="embedded-message-template-modal-actions"
        hasCancel={isCreateTemplateFormEnabled}
      >
        {isCreateTemplateFormEnabled ? (
          <PrimaryButton
            data-test="embedded-message-template-modal-create-template-button"
            isDisabled={!isCreateButtonEnabled}
            isLoading={isTemplateAsyncProcessing}
            onClick={handleClick}
          >
            {createTemplateButtonText}
          </PrimaryButton>
        ) : (
          <SecondaryButton onClick={closeModal}>{okayLabel}</SecondaryButton>
        )}
      </ModalFooter>
    </StyledModal>
  );
};
